const keyBy = require('lodash/keyBy')
const get = require('lodash/get')
const { resolveWarehouseByLocationId } = require('../../order/tools/read/warehouse-code-to-virtual')

const getShipmentVolumes = async (shipmentIds, api) => {
  try {
    return (await api.shipment.calculateVolume({ shipmentIds }))
      .reduce((acc, {
        shipment_id: shipmentId,
        total_volume: totalVolume
      }) => {
        return {
          ...acc,
          [shipmentId]: totalVolume
        }
      }, {})
  } catch (error) {
    console.error('Error calculating shipment volume!', error.message)
    return {}
  }
}

const getProgram = (programs, programId) =>
  programs.find(p => p.id === programId)

// The function checks for locations that are PSM warehouses and ensures that
// their corresponding real warehouse locations are also included in the list
// if they were missing.
const fillMissingWarehouseOrigins = async ({ api, locations }) => {
  const { missingLocationIds, virtualSubstitutionMap } = locations.reduce((acc, location) => {
    const isPSMWarehouse = get(location, 'additionalData.isPSMWarehouse', false)
    if (isPSMWarehouse) {
      // Resolve the real warehouse location id from the virtual id
      try {
        const { _id: warehouseLocationId } = resolveWarehouseByLocationId(location._id)
        // If the real warehouse location id is already in the locations list, we skip it
        return warehouseLocationId === location._id
          ? { ...acc }
          : {
            missingLocationIds: [...acc.missingLocationIds, warehouseLocationId],
            virtualSubstitutionMap: { ...acc.virtualSubstitutionMap, [location._id]: warehouseLocationId }
          }
      } catch (_) {
        // RMS tenants always trigger the following error:
        // "Error: Warehouse for locationId country:rw:......... not found"
        // This check prevents the route page from breaking.
      }
    }
    return acc
  }, { missingLocationIds: [], virtualSubstitutionMap: {} })

  if (missingLocationIds.length === 0) {
    return { locations, virtualSubstitutionMap: {} }
  }

  const missingLocations = await api.location.getByIds(missingLocationIds)

  return {
    locations: [
      ...locations,
      ...missingLocations
    ],
    virtualSubstitutionMap
  }
}

/**
 * Gets the origin data for a route.
 *
 * Origin return behavior:
 * - For routes with a single origin: returns { origin: Object, realOriginIds: string[] }
 * - For routes with multiple origins: throws Error (data integrity issue)
 * - For routes with no origins: returns { origin: null, realOriginIds: string[] }
 *
 * @param {Object} params
 * @param {string} params.routeId - The route ID
 * @param {Set} params.originIds - Set of origin IDs
 * @param {Object} params.locationsById - Map of location IDs to location objects
 * @param {Object} params.virtualSubstitutionMap - Map of virtual to real warehouse IDs
 * @returns {{origin: Object|null, realOriginIds: Array<string>}}
 */
const getOriginData = ({ routeId, originIds, locationsById, virtualSubstitutionMap }) => {
  // Here we get a uniqe list of real origins by substituting any virtual warehouse
  // with the physical warehouse
  const realOrigins = new Set(
    Array.from(originIds).map(originId => virtualSubstitutionMap[originId] || originId)
  )
  const realOriginIds = Array.from(realOrigins)

  if (realOrigins.size === 1) {
    const realOriginId = realOriginIds[0]
    return {
      origin: locationsById[realOriginId],
      realOriginIds
    }
  }

  if (realOrigins.size === 0) {
    return {
      origin: null,
      realOriginIds: []
    }
  }

  // Multiple origins found - data integrity issue
  throw new Error(`Multiple origins found for route ${routeId} after resolving virtual warehouses!`)
}

/**
 * Decorates routes with detailed information including shipments, locations, funders, and programs.
 *
 * @param {Object} params
 * @param {Array} params.routes - Array of routes to decorate
 * @param {Object} api - The API object
 * @returns {Promise<Array>} Decorated routes
 */
const decorateRouteWithDetails = async ({ routes }, api) => {
  if (!routes || routes.length === 0) {
    return []
  }

  const shipments = routes.reduce((acc, route) => {
    return [
      ...acc,
      ...(route.shipments || [])
    ]
  }, [])

  const locationAndFunderAndProgramIds = shipments.reduce((acc, curr) => {
    acc.locations.add(curr.destination_id)
    acc.locations.add(curr.origin_id)
    if (curr.funder_id) {
      acc.funders.add(curr.funder_id)
    }
    if (curr.program_id) {
      acc.programs.add(curr.program_id)
    }
    return acc
  }, { locations: new Set(), funders: new Set(), programs: new Set() })

  // We get all programs because:
  // - Carriers are not limited by a program set.
  // - Due to a bug in the function that fetches routes, all users are
  //   getting all routes, regardless of their program. This needs to be
  //   fixed properly, but until then, we don't want decorateRouteWithDetails
  //   to throw because the program cannot be found.
  const allPrograms = locationAndFunderAndProgramIds.programs.size > 0 ? await api.program.list({ unfiltered: true }) : []
  const programs = Array.from(locationAndFunderAndProgramIds.programs).map((programId) => getProgram(allPrograms, programId))

  const [locations, funders] = await Promise.all([
    api.location.getByIds([...locationAndFunderAndProgramIds.locations]),
    api.funders.list({ unfiltered: false, programIds: programs.map(p => p.id) })
  ])

  const { locations: filledLocations, virtualSubstitutionMap } =
    await fillMissingWarehouseOrigins({ api, locations })

  const locationsById = keyBy(filledLocations, '_id')
  const fundersById = keyBy(funders, '_id')
  const programsById = keyBy(programs, 'id')

  const shipmentsByIds = keyBy(shipments, 'shipment_id')
  const shipmentVolumes = await getShipmentVolumes(Object.keys(shipmentsByIds), api)

  return (
    routes
      .map((route) => {
        // Collect the shipments that belong to this route
        // and add the calculated load volume to each shipment
        const routeShipments = (route.shipments || []).map((shipment) => {
          const loadVolume = parseFloat(shipmentVolumes[shipment.shipment_id])
          return {
            ...shipment,
            loadVolume: isNaN(loadVolume) ? 0 : loadVolume,
            origin: locationsById[shipment.origin_id],
            destination: locationsById[shipment.destination_id],
            funder: fundersById[shipment.funder_id],
            program: programsById[shipment.program_id]
          }
        })

        // Collect the unique LGAs, the total load volume and number of stops of the route
        const routeShipmentsData = routeShipments.reduce(
          (acc, shipment) => {
            const destinationId = shipment.destination_id
            const lga = get(locationsById, `${destinationId}.location.lga`, null)
            acc.lga.add(lga)
            const state = get(locationsById, `${destinationId}.location.state`, null)
            acc.state.add(state)
            acc.destinations.add(locationsById[destinationId])
            acc.origin.add(shipment.origin_id)
            acc.funders.add(shipment.funder_id)
            acc.programs.add(shipment.program_id)
            acc.totalLoadVolume += shipment.loadVolume
            return acc
          },
          {
            lga: new Set(),
            totalLoadVolume: 0,
            state: new Set(),
            destinations: new Set(),
            origin: new Set(),
            funders: new Set(),
            programs: new Set()
          }
        )

        const { origin } = getOriginData({
          locationsById,
          virtualSubstitutionMap,
          routeId: route.couchdb_id,
          originIds: routeShipmentsData.origin
        })

        return {
          id: route.couchdb_id,
          name: route.name,
          lga: Array.from(routeShipmentsData.lga).join(', '),
          state: Array.from(routeShipmentsData.state).join(', '),
          origin: origin,
          destinations: Array.from(routeShipmentsData.destinations),
          stopsNo: routeShipmentsData.destinations.size,
          load: routeShipmentsData.totalLoadVolume,
          createdAt: route.created_at,
          startDate: route.start_date,
          endDate: route.end_date,
          approvalDate: route.approval_date
            ? route.approval_date
            : null,
          estimatedDeliveryDate: route.estimated_delivery_date
            ? route.estimated_delivery_date
            : null,
          estimatedPickupDate: route.estimated_pickup_date
            ? route.estimated_pickup_date
            : null,
          funders: Array.from(routeShipmentsData.funders).map(funderId => fundersById[funderId]),
          programs: Array.from(routeShipmentsData.programs).map(programId => programsById[programId]),
          status: route.status,
          shipments: routeShipments,
          carrier: get(route, 'carrier', undefined),
          accepted: route.accepted
        }
      })

      // Filter out routes that have invalid origins.
      .filter(route => {
        if (!route.name) return false
        if (route.shipments.length === 0) return true
        return !!route.origin
      })
  )
}
module.exports = {
  decorateRouteWithDetails,
  fillMissingWarehouseOrigins, // exported for testing purposes only
  getOriginData // exported for testing purposes only
}
